<template>
  <v-container fluid class='ma-0 pa-0'>

    <!-- File Input Elements -->
    <template>
      <input
        v-show="false"
        type="file"
        ref="audio"
        accept="audio/mp3"
        @change="fileChanged('audio')"
      />

      <input
        v-show="false"
        type="file"
        ref="image"
        accept="image/*"
        @change="fileChanged('image')"
      />

      <input
        v-show="false"
        type="file"
        ref="video"
        accept="video/mp4"
        @change="fileChanged('video')"
      />
    </template>

    <!-- Input Form -->
    <v-dialog v-model="visible" :max-width="formWidth" persistent>
      <v-card>
        <v-toolbar dense dark flat :color="toolbarColorDark">
          <v-toolbar-title>
            <v-icon class="mr-2">mail</v-icon>{{ getExpression('FormTitle', 'CreateMessage') }}
          </v-toolbar-title>
        </v-toolbar>
  
          <v-card-text class="pa-1 pt-2">
            <v-container grid-list-md class="pt-0 pb-0">
              <form>

              <template v-if="isMultipleResidents">
                <v-alert
                  v-if="showResidentAlert"
                  border="top"
                  colored-border
                  type="info"
                  elevation="2"
                  class="pt-2 pb-0 px-1"
                  >
                  <v-row align="center">
                    <v-col cols="12" sm="10">
                      <span class="body-2" v-html="getExpression('Alert', 'MessageResidentSelectionText')"></span>
                    </v-col>
                    <v-col cols="12" sm="2">
                      <v-icon color="#333">arrow_downward</v-icon>
                    </v-col>
                  </v-row>
                </v-alert>

                <!-- Resident(s) -->
                <resident-selector
                  v-model="residentIds"
                  :options="selectorResidents"
                  :label="getExpression('Label', 'AssignedResidents')"
                  v-validate="'required'"
                  :error="errors.first('resident')"
                  data-vv-name="resident"
                  ref="residents"
                />
              </template>
              
              <!-- Text Message -->
              <template v-if="isText">
                <v-textarea
                  v-model="text"
                  :label="getExpression('MenuItem', 'Text')"
                  :disabled="isSending"
                  v-validate="isText ? 'required' : ''"
                  :error-messages="errors.collect('text')"
                  data-vv-name="text"
                  auto-grow
                  ref="text"
                  clearable
                  >
                </v-textarea>
              </template>

              <!-- Title & Media -->
              <template v-else-if="blobProperties.length > 0">
                <v-text-field
                  v-model="title"
                  :label="getExpression('Label', 'Title')"
                  :disabled="isSending"
                  v-validate="'required|max:50'"
                  :error-messages="errors.collect('title')"
                  data-vv-name="title"
                  ref="title"
                  clearable
                  >
                </v-text-field>

                <template v-if="blobProperties.length > 0">
                  <media-properties-form
                    v-bind:blob="blobProperties[0]"
                    v-bind:disableEdit="true"
                    v-bind:showRemoveButton="true"
                    v-on:remove="removeMediaItem"
                    class="pt-4"
                  />
                </template>
              </template>
            </form>
          </v-container>
        </v-card-text>

        <v-card-actions class="pb-4">
          <v-btn v-if="showAddContentButton" tile @click="messageTypeDialog = true" class="ml-3 pr-3">
            {{ getExpression('Button', 'AddContent') }}
          </v-btn>

          <v-spacer />
  
          <v-btn @click="cancel" :loading="isSending" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
          <v-btn v-if="!disableSend" :color="toolbarColorDark" dark @click="upload" :loading="isSending" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Send') }}</v-btn>
          <v-btn v-else disabled class="rounded-0 ml-4" large>{{ getExpression('Button', 'Send') }}</v-btn>
  
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Message Type Selector -->
    <v-dialog v-model="messageTypeDialog" :max-width="messageTypeSelectorWidth" persistent>
      <message-type-selector
        v-on:close="closeChooseMessageType"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
      />
    </v-dialog>

  </v-container>
</template>
  
<script>
import { mapGetters } from 'vuex'
import AppMessage from '@/plugins/message.js'
import MessageTypeSelector from '@/components/MessageTypeSelector'
import ResidentSelector from '@/components/ResidentSelector'
import MediaPropertiesForm from '@/components/MediaPropertiesForm'
import { AppColor, LanguageId, MessageTypeId, ImageUri } from '@/config.js'

export default {
  name: 'AppMessage',
  components: {
    MessageTypeSelector,
    ResidentSelector,
    MediaPropertiesForm
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {

      // colors
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // message types
      messageTypeId_UserToResident: MessageTypeId.UserToResident,

      // uri
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,
      
      // message
      title: null,
      text: null,
      blobProperties: [],
      files: [],
      mediaFile: null,
      isText: false,
      residentIds: [],

      // boolean
      isSending: false,
      visible: false,
      messageTypeDialog: false,

      // params
      selectorResidents: [],
      onSend: {},
      onCancel: {},
      isMultipleResidents: true
    }
  },
  computed: {
    ...mapGetters(['profile', 'activeKiosk', 'locale']),
    toolbarColor () {
      return this.appColor_Resident
    },
    toolbarColorDark () {
      return this.appColor_ResidentDark
    },
    dictionary () { 
      return {
        custom: {
          title: {
            required: () => this.getExpression('Validation', 'TitleRequired'),
            max: this.getExpression('Validation', 'TitleMaxChars').replace('{0}', '50')
          },
          resident: {
            required: () => this.getExpression('Validation', 'ResidentRequired')
          },
          text: {
            required: () => this.getExpression('Validation', 'MessageTextRequired')
          }
        }
      }
    },
    disableSend () {
      return this.blobProperties.length === 0 && this.text === null
    },
    showAddContentButton () {
      return (this.residentIds ?? []).length > 0
        && (this.blobProperties ?? []).length === 0
        && !this.isText
    },
    showResidentAlert () {
      return (this.residentIds ?? []).length === 0
    },
    formWidth () {
      if (this.isNullOrUndefined(this.profile)) return 380
      return this.profile.languageId === LanguageId.English
        ? 380
        : 450
    },
    messageTypeSelectorWidth () {
      if (this.isNullOrUndefined(this.profile)) return 180
      return this.profile.languageId === LanguageId.English
        ? 180
        : 200
    }
  },
  methods: {
    compose (params) {
      this.initParams(params)

      this.residentIds = []
      this.title = null
      this.text = null
      this.blobProperties = []
      this.files = []
      this.mediaFile = null
      this.isText = false

      this.reset()

      // in multi-resident mode show the main form
      if (this.isMultipleResidents) {
        this.messageTypeDialog = false
        this.visible = true

      // in single resident mode show the message type selector
      } else {
        this.messageTypeDialog = true
        this.visible = false
      }

      if (this.selectorResidents.length === 1 && !this.isMultipleResidents) {
        this.residentIds = [this.selectorResidents[0].id]
      } else {
        this.setFocus('residents')
      }
    },

    initParams (params) {
      if (this.isNullOrUndefined(params)) return
      if (!this.isNullOrUndefined(params.onSend)) this.onSend = params.onSend
      if (!this.isNullOrUndefined(params.onCancel)) this.onCancel = params.onCancel
      if (!this.isNullOrUndefined(params.isMultipleResidents)) this.isMultipleResidents = params.isMultipleResidents
      else this.isMultipleResidents = true
      if (!this.isNullOrUndefined(params.residents)) this.selectorResidents = params.residents
    },

    fileChanged (ref) {
      const self = this

      const files = self.$refs[ref].files
      if (!files.length > 0) {
        self.$error.show({ text: 'No file was selected.' })
      }

      const file = files[0]
      self.mediaFile = file
      self.blobProperties = []
      const ext = file.name.split('.').pop()

      var previewUrl = ''
      const mediaType = self.getMediaType(file.type)

      //-- image rules
      if (ref === 'image' && mediaType === 'image') {
        if (ext === 'webp') {
          self.$alert.show({ 
            text: self.getExpression('Validation', 'FileTypeNotAccepted').replace('{0}', 'webp')
          })
        }

        previewUrl = URL.createObjectURL(file)

      //-- video rules
      } else if (ref === 'video' && mediaType === 'video' &&
        ['video/mp4'].includes(file.type)
      ) {
        previewUrl = self.imageUri_AudioVideoPlaceholder

      //-- audio rules
      } else if (ref === 'audio' && mediaType === 'audio' 
        && ['audio/mp3', 'audio/mp4', 'audio/mpeg'].includes(file.type)
      ) {
        previewUrl = self.imageUri_AudioVideoPlaceholder
      }

      self.mediaFile = file
      
      self.blobProperties = [{
        blobName: file.name,
        contentType: file.type,
        size: file.size,
        previewUrl: previewUrl,
        title: self.getDefaultTitle(file.name),
        description: null
      }]

      if (self.blobProperties.length > 0) {
        self.$refs[ref].value = null

        // in single-resident mode the main form will be hidden (and therefore needs to be unhidden)
        if (!self.isMultipleResidents) self.visible = true

        self.setFocus('title')
      } else {
        var errorText = ''
        const languagedContentType = self.getContentTypeAbbr(ref)

        if (ref === 'image') {
          errorText = self.getExpression('Alert', 'SelectValidFiles').replace('{0}', languagedContentType)
        } else if (ref === 'video') {
          errorText = self.getExpression('Alert', 'FileTypeNotAccepted').replace('{0}', ext)
        } else if (ref === 'audio') {
          errorText = self.getExpression('Alert', 'OnlyMp3Supported')
        }
        self.$error.show({
          title: self.getExpression('FormTitle', 'InvalidMedia'),
          severity: 0,
          text: errorText
        })
      }
    },

    upload () {
      const self = this

      self.$validator.resume()
      self.$validator.validateAll().then(valid => {

        if (valid) {
          let formData = new FormData()

          if (self.isText) {
            formData.append('textMessage', self.text)
          } else {
            formData.append('file', self.mediaFile)
          }
        
          const today = self.$moment(new Date())
          const description = `Message from ${self.profile.name} on ${today.format('MMM-DD-YYYY')}`

          formData.append('subject', self.title)
          formData.append('description', description)
          const residentIds = self.residentIds.join(',')
          formData.append('residentId', residentIds)
          formData.append('userId', self.profile.id)
          formData.append('messageTypeId', self.messageTypeId_UserToResident)
          formData.append('deviceInfo', 'Admin Site')
          
          var uploadRoute = 'text-only'
          if (!self.isText) {
            const mediaType = self.getMediaType(self.blobProperties[0].contentType)

            if (mediaType === 'image') {
              uploadRoute = 'images'
            } else if (mediaType === 'video') {
              uploadRoute = 'videos'
            } else if (mediaType === 'audio') {
              uploadRoute = 'audio'
            }
          }

          self.$upload.execute({
            formData: formData,
            url: `messages/upload/${uploadRoute}`,
            fileCount: 1,
            progressExpression: 'SendingMessage',
            onComplete: (data) => {
              self.propsDialog = false
              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                self.$alert.show({
                  formColor: 'success',
                  title: self.getExpression('Label', 'Success'),
                  icon: 'done',
                  text: self.getExpression('Alert', 'MessageSuccessfullySent'),
                  onClose: () => {
                    self.visible = false
                    if (typeof self.onSend === 'function') {
                      self.onSend(self.residentIds)
                    }
                    setTimeout(() => { self.reset() }, 300)
                  }
                })
              }
            }
          })
        }
      })
    },

    cancel () {
      const self = this

      if (typeof self.onCancel === 'function') {
        self.onCancel()
        self.visible = false
      } else {
        self.visible = false
      }
      setTimeout(() => { self.reset() }, 300)
    },

    closeChooseMessageType (messageType) {
      this.messageTypeDialog = false

      switch (messageType) {
        case 'audio':
          this.$refs.audio.click()
          break
        case 'image':
          this.$refs.image.click()
          break
        case 'video':
          this.$refs.video.click()
          break
        case 'text':
          // unhide the main form if not in multi-resident mode
          if (!this.isMultipleResidents) this.visible = true
          this.isText = true
          this.setFocus('text')
          break;
      }    
    },

    removeMediaItem () {
      this.blobProperties = []
      this.mediaFile = null
    },

    reset () {
      this.residentIds = []
      this.$validator.reset()
      this.$validator.pause()
    },

    setFocus (refName) {
      const self = this

      setTimeout(() => {
        switch(refName)
        {
          case 'residents':
            self.$refs.residents.focus()
            return
          case 'title':
            self.$refs.title.focus()
            return
          case 'text':
            self.$refs.text.focus()
            return
        }            
      }, 300)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    AppMessage.EventBus.$on('compose', (params) => {
      this.compose(params)
    })
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  }
}
</script>